import React, { useState, useContext } from 'react';
import { CurpContext } from '../context/CurpContext';
import { motion } from 'framer-motion';
import Logo from '../components/Logo';
import { useNavigate, useLocation } from 'react-router-dom';
import BackgroundLayout from '../components/BackgroundLayout';
import Label from '../components/form/Label';
import { toast } from 'react-hot-toast';

const MedicalRecord = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { curp } = useContext(CurpContext);
    const buttonsContainer = "flex w-full space-x-4 mt-5"

    const [formData, setFormData] = useState({
        curp: curp,
        disease_history: [],
        medication_allergies: '',
        allergies: '',
        substances: []
    });

    const [showInputMedicationAllergies, setShowInputMedication] = useState(false);
    const [showInputAllergies, setShowInputAllergies] = useState(false);
    const [selectedMedicationButton, setSelectedMedicationButton] = useState(null);
    const [selectedAllergiesButton, setSelectedAllergiesButton] = useState(null);
    const inputClass = "border p-2 rounded-md w-full drop-shadow-md mt-5";

    const handleButtonClick = (name, value) => {
        if (name === 'medication_allergies') {
            setShowInputMedication(value);
            setSelectedMedicationButton(value);
        }
        if (name === 'allergies') {
            setShowInputAllergies(value);
            setSelectedAllergiesButton(value);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value })
    };

    const handleToggle = (field, value) => {
        setFormData(prevState => {
            const currentArray = prevState[field];
            if (value === 'Ninguna') {
                return { ...prevState, [field]: ['Ninguna'] };
            } else {
                const newArray = currentArray.includes(value)
                    ? currentArray.filter(item => item !== value)
                    : [...currentArray.filter(item => item !== 'Ninguna'), value];
                return { ...prevState, [field]: newArray };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/services/register-medical-history/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                toast.success('Genial, ¿Cuándo nos visitas?')
                if (location.pathname.includes('/services/vaccine')) {
                    navigate('/services/vaccine/medical-record/appointment'); // Redirige a una página de éxito
                } else {
                    navigate('/services/medical-service/medical-record/appointment'); // Redirige a una página de éxito
                }
            } else {
                toast.error('Ay, algo pasó, intenta nuevamente.');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3, // Aumenta el tiempo entre la aparición de cada hijo
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 2,
            y: 0,
            transition: {
                duration: 1, // Aumenta la duración de la animación
                ease: "easeInOut", // Suaviza la animación
            },
        },
        exit: {
            opacity: 0,
            y: 20,
            transition: {
                duration: 1, // Aumenta la duración de la animación de salida
                ease: "easeInOut", // Suaviza la animación de salida
            },
        },
    };

    return (
        <BackgroundLayout className="px-8 pb-24">
            <motion.div
                className="w-full text-left mt-0 lg:w-2/3 lg:mb-10"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Logo />
                <motion.h1 initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.5 }} className='text-center text-7xl font-bold text-[#f8485e] mt-10 mb-2 drop-shadow-sm'>📝</motion.h1>
                <h1 className='text-5xl font-bold text-[#f8485e] mt-10 uppercase drop-shadow-sm'>Historia clínica</h1>
                <h2 className="text-base text-justify font-bold mt-5 mb-5 text-gray-800 uppercase drop-shadow-sm">¡Vamos a hacer esto rápido y sin dolor! Solo unas cuantas preguntas para conocerte mejor. 🩺</h2>

                <form onSubmit={handleSubmit}>
                    <div className="mt-5">
                        <h3 className="text-gray-700 font-bold uppercase text-sm mb-3">Antecedentes de enfermedades (puedes seleccionar más de una opción):</h3>
                        <motion.div variants={containerVariants} className="grid grid-cols-3 gap-2 mt-2">
                            {['Renales', 'Hipertensión', 'Obesidad', 'Óseo', 'Salud mental', 'Cardiacos', 'Cáncer', 'Ninguna'].map((item, index) => (
                                <motion.button
                                    variants={itemVariants}
                                    key={index}
                                    type="button"
                                    className={`p-2 border-2 text-center rounded-md drop-shadow-md ${formData.disease_history.includes(item) ? 'bg-inspiraPink text-white' : 'bg-white text-inspiraPink border-inspiraPink'}`}
                                    onClick={() => handleToggle('disease_history', item)}
                                >
                                    {item}
                                </motion.button>
                            ))}
                        </motion.div>
                    </div>
                    <motion.div variants={containerVariants} className="mb-4 mt-5">
                        <Label text='¿Eres alergico a algún medicamento?' />
                        <motion.div className={buttonsContainer} variants={containerVariants}>
                            <div className='w-1/2 flex justify-center'>
                                <motion.button
                                    variants={itemVariants}
                                    type="button"
                                    onClick={() => handleButtonClick('medication_allergies', true)}
                                    className={`rounded-md border-2 drop-shadow-md p-2 m-2 ${selectedMedicationButton === true ? 'bg-inspiraPink text-white border-inspiraPink' : 'bg-inspiraSoftPink text-inspiraPink border-inspiraPink'}`}
                                >
                                    Sí
                                </motion.button>
                            </div>
                            <div className='w-1/2 flex justify-center'>
                                <motion.button
                                    variants={itemVariants}
                                    type="button"
                                    onClick={() => handleButtonClick('medication_allergies', false)}
                                    className={`w-full rounded-md border-2 drop-shadow-md p-4 m-2 text-center font-bold uppercase transition-all ${selectedMedicationButton === false ? 'bg-inspiraPink text-white border-inspiraPink' : 'bg-white text-inspiraPink border-inspiraPink'}`}
                                >
                                    No
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>

                    {showInputMedicationAllergies && (
                        <motion.div variants={containerVariants}>
                            <Label text='¿A qué medicamento?' />
                            <motion.input
                                variants={itemVariants}
                                name="medication_allergies"
                                value={formData.medication_allergies}
                                onChange={handleChange}
                                className={inputClass}
                            />
                        </motion.div>
                    )}
                    <motion.div variants={containerVariants} className="mb-4 mt-5">
                        <Label text='¿Eres alergico a alguna otra cosa?' />
                        <motion.div className={buttonsContainer} variants={containerVariants}>
                            <div className='w-1/2 flex justify-center'>
                                <motion.button
                                    variants={itemVariants}
                                    type="button"
                                    onClick={() => handleButtonClick('allergies', true)}
                                    className={`rounded-md border-2 drop-shadow-md p-2 m-2 ${selectedAllergiesButton === true ? 'bg-inspiraPink text-white border-inspiraPink' : 'bg-inspiraSoftPink text-inspiraPink border-inspiraPink'}`}
                                >
                                    Sí
                                </motion.button>
                            </div>
                            <div className='w-1/2 flex justify-center'>
                                <motion.button
                                    variants={itemVariants}
                                    type="button"
                                    onClick={() => handleButtonClick('allergies', false)}
                                    className={`w-full rounded-md border-2 drop-shadow-md p-4 m-2 text-center font-bold uppercase transition-all ${selectedAllergiesButton === false ? 'bg-inspiraPink text-white border-inspiraPink' : 'bg-white text-inspiraPink border-inspiraPink'}`}
                                >
                                    No
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                    {showInputAllergies && (
                        <motion.div variants={containerVariants}>
                            <Label text='¿A qué eres alergico?' />
                            <motion.input
                                variants={itemVariants}
                                name="allergies"
                                value={formData.allergies}
                                onChange={handleChange}
                                className={inputClass}
                            />
                        </motion.div>
                    )}

                    <div className="mt-5">
                        <h3 className="text-gray-700 font-bold uppercase text-sm mb-3">¿Consumes alguna de estas sustancias?</h3>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                            {['Tabaco', 'Alcohol', 'Marihuana', 'Otras sustancias', 'Ninguna'].map((item, index) => (
                                <motion.button
                                    variants={itemVariants}
                                    key={index}
                                    type="button"
                                    className={`p-2 border-2 rounded-md drop-shadow-md ${formData.substances.includes(item) ? 'bg-inspiraPink text-white' : 'bg-white text-inspiraPink border-inspiraPink'}`}
                                    onClick={() => handleToggle('substances', item)}
                                >
                                    {item}
                                </motion.button>
                            ))}
                        </div>
                    </div>
                    <motion.div className='w-full flex justify-center mt-5 mb-24'>
                        <motion.button type="submit" className="mt-5 p-4 bg-[#f8485e] hover:bg-[#d93a4f] text-white rounded-xl shadow-lg w-2/3 text-lg font-bold uppercase transition-all active:scale-95">Enviar</motion.button>
                    </motion.div>
                </form>
            </motion.div>
        </BackgroundLayout>
    );
};

export default MedicalRecord;